function normalizeSearchText(value) {
  return String(value ?? '')
    .normalize('NFKC')
    .toLocaleLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function productMatches(searchText, query) {
  const normalizedQuery = normalizeSearchText(query);

  if (!normalizedQuery) return true;

  return normalizeSearchText(searchText).includes(normalizedQuery);
}

function selectActiveSeries(seriesPositions, activationLine, isAtPageBottom, requestedSeriesId) {
  const visibleSeries = seriesPositions.filter((series) => !series.hidden);

  if (visibleSeries.length === 0) return undefined;
  if (visibleSeries.some((series) => series.id === requestedSeriesId)) return requestedSeriesId;
  if (isAtPageBottom) return visibleSeries[visibleSeries.length - 1].id;

  let activeSeriesId = visibleSeries[0].id;

  for (const series of visibleSeries) {
    if (series.top > activationLine) break;
    activeSeriesId = series.id;
  }

  return activeSeriesId;
}

function getMobileToolbarState(isStuck, activeSeriesTitle, defaultLabel) {
  return {
    label: isStuck && activeSeriesTitle ? activeSeriesTitle : defaultLabel,
    showResultCount: !isStuck,
  };
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    getMobileToolbarState,
    normalizeSearchText,
    productMatches,
    selectActiveSeries,
  };
}

if (typeof window !== 'undefined' && typeof customElements !== 'undefined') {
  class AccessoriesCatalog extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.searchInput = this.querySelector('[data-catalog-search]');
      this.clearButton = this.querySelector('[data-catalog-clear]');
      this.resultCount = this.querySelector('[data-catalog-result-count]');
      this.noResults = this.querySelector('[data-catalog-no-results]');
      this.groups = Array.from(this.querySelectorAll('[data-catalog-series]'));
      this.cards = Array.from(this.querySelectorAll('[data-catalog-product]'));
      this.navLinks = Array.from(this.querySelectorAll('[data-catalog-nav-link]'));
      this.toolbar = this.querySelector('[data-catalog-toolbar]');
      this.toolbarSentinel = this.querySelector('[data-catalog-toolbar-sentinel]');
      this.drawer = this.querySelector('[data-catalog-drawer]');
      this.drawerTrigger = this.querySelector('[data-catalog-drawer-open]');
      this.drawerTriggerLabel = this.querySelector('[data-catalog-drawer-label]');
      this.drawerDefaultLabel = this.drawerTriggerLabel?.dataset.defaultLabel || '';
      this.abortController = new AbortController();
      this.searchTimer = null;
      this.seriesFrame = null;
      this.navigationTargetId = null;
      this.navigationReleaseTimer = null;
      this.activeSeriesId = null;
      this.isToolbarStuck = false;

      this.bindEvents();
      this.applyFilter('');
      this.setupSeriesTracking();
    }

    disconnectedCallback() {
      window.clearTimeout(this.searchTimer);
      window.clearTimeout(this.navigationReleaseTimer);
      window.cancelAnimationFrame(this.seriesFrame);
      this.abortController?.abort();

      if (this.drawer?.open) this.drawer.close();
      document.body.classList.remove('accessories-catalog--drawer-open');
      delete this.dataset.initialized;
    }

    bindEvents() {
      const { signal } = this.abortController;

      this.searchInput?.addEventListener('input', () => this.queueFilter(), { signal });
      this.searchInput?.addEventListener('search', () => this.queueFilter(), { signal });
      this.clearButton?.addEventListener(
        'click',
        () => {
          this.searchInput.value = '';
          this.applyFilter('');
          this.searchInput.focus();
        },
        { signal }
      );

      this.navLinks.forEach((link) => {
        link.addEventListener('click', (event) => this.navigateToSeries(event), { signal });
      });

      this.drawerTrigger?.addEventListener('click', () => this.openDrawer(), { signal });
      this.querySelectorAll('[data-catalog-drawer-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeDrawer(), { signal });
      });

      this.drawer?.addEventListener(
        'click',
        (event) => {
          if (event.target === this.drawer) this.closeDrawer();
        },
        { signal }
      );
      this.drawer?.addEventListener(
        'close',
        () => {
          document.body.classList.remove('accessories-catalog--drawer-open');
          this.drawerTrigger?.setAttribute('aria-expanded', 'false');
          this.drawerTrigger?.focus();
        },
        { signal }
      );
    }

    queueFilter() {
      window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => this.applyFilter(this.searchInput.value), 150);
    }

    applyFilter(query) {
      let visibleProductCount = 0;
      const normalizedQuery = normalizeSearchText(query);

      this.groups.forEach((group) => {
        const groupCards = Array.from(group.querySelectorAll('[data-catalog-product]'));
        let visibleGroupCount = 0;

        groupCards.forEach((card) => {
          const isMatch = productMatches(card.dataset.searchText, normalizedQuery);
          card.hidden = !isMatch;

          if (isMatch) {
            visibleGroupCount += 1;
            visibleProductCount += 1;
          }
        });

        group.hidden = visibleGroupCount === 0;
        this.querySelectorAll(`[data-series-id="${CSS.escape(group.dataset.seriesId)}"]`).forEach((navItem) => {
          navItem.hidden = visibleGroupCount === 0;
        });
      });

      this.updateResultState(visibleProductCount, normalizedQuery);
      this.syncActiveSeries();
    }

    updateResultState(visibleProductCount, normalizedQuery) {
      const singularLabel = this.dataset.resultSingular || 'product';
      const pluralLabel = this.dataset.resultPlural || 'products';
      const resultLabel = visibleProductCount === 1 ? singularLabel : pluralLabel;

      if (this.resultCount) {
        this.resultCount.textContent = `${visibleProductCount} ${resultLabel}`;
      }

      if (this.clearButton) {
        this.clearButton.hidden = !normalizedQuery;
      }

      if (this.noResults) {
        this.noResults.hidden = visibleProductCount !== 0;
      }
    }

    setupSeriesTracking() {
      const { signal } = this.abortController;
      const handleScroll = () => {
        this.queueSeriesSync();
        this.scheduleNavigationRelease();
      };

      window.addEventListener('scroll', handleScroll, { passive: true, signal });
      window.addEventListener('resize', () => this.queueSeriesSync(), { passive: true, signal });
      this.syncActiveSeries();
    }

    queueSeriesSync() {
      if (this.seriesFrame !== null) return;

      this.seriesFrame = window.requestAnimationFrame(() => {
        this.seriesFrame = null;
        this.syncActiveSeries();
      });
    }

    syncActiveSeries() {
      this.syncToolbarState();
      const activationLine = this.getActivationLine();
      const seriesPositions = this.groups.map((group) => ({
        id: group.id,
        top: group.getBoundingClientRect().top,
        hidden: group.hidden,
      }));
      const isAtPageBottom =
        Math.ceil(window.scrollY + window.innerHeight) >= document.documentElement.scrollHeight - 2;

      this.setActiveSeries(
        selectActiveSeries(seriesPositions, activationLine, isAtPageBottom, this.navigationTargetId)
      );
    }

    getVisibleHeaderBottom() {
      const stickyHeader = document.querySelector('.section-header');
      const stickyHeaderBottom = stickyHeader?.getBoundingClientRect().bottom;
      const configuredHeaderHeight =
        Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const visibleHeaderBottom = Number.isFinite(stickyHeaderBottom)
        ? Math.max(0, stickyHeaderBottom)
        : configuredHeaderHeight;

      return visibleHeaderBottom;
    }

    syncToolbarState() {
      if (!this.toolbar || !this.toolbarSentinel) return;

      const visibleHeaderBottom = this.getVisibleHeaderBottom();
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      const sentinelTop = this.toolbarSentinel.getBoundingClientRect().top;

      this.style.setProperty('--accessories-sticky-top', `${visibleHeaderBottom}px`);
      this.isToolbarStuck = isMobile && sentinelTop <= visibleHeaderBottom;
      this.toolbar.classList.toggle('is-stuck', this.isToolbarStuck);
      this.style.setProperty('--accessories-toolbar-height', `${this.toolbar.offsetHeight}px`);
      this.updateMobileToolbar();
    }

    getActivationLine() {
      const toolbarHeight = this.isToolbarStuck ? this.toolbar?.offsetHeight || 0 : 0;

      return this.getVisibleHeaderBottom() + toolbarHeight + 24;
    }

    setActiveSeries(seriesId) {
      this.activeSeriesId = seriesId;
      this.navLinks.forEach((link) => {
        const isActive = Boolean(seriesId) && link.dataset.target === seriesId;
        link.classList.toggle('is-active', isActive);

        if (isActive) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });
      this.updateMobileToolbar();
    }

    updateMobileToolbar() {
      if (!this.drawerTriggerLabel) return;

      const activeSeriesTitle =
        this.groups.find((group) => group.id === this.activeSeriesId && !group.hidden)?.dataset.seriesTitle || '';
      const toolbarState = getMobileToolbarState(
        this.isToolbarStuck,
        activeSeriesTitle,
        this.drawerDefaultLabel
      );

      this.drawerTriggerLabel.textContent = toolbarState.label;
      if (this.resultCount) this.resultCount.hidden = !toolbarState.showResultCount;
    }

    navigateToSeries(event) {
      const link = event.currentTarget;
      const target = this.querySelector(`#${CSS.escape(link.dataset.target)}`);

      if (!target || target.hidden) return;

      event.preventDefault();
      this.closeDrawer();
      this.navigationTargetId = target.id;
      this.scheduleNavigationRelease();
      this.setActiveSeries(target.id);
      history.pushState(null, '', `#${target.id}`);
      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    }

    scheduleNavigationRelease() {
      if (!this.navigationTargetId) return;

      window.clearTimeout(this.navigationReleaseTimer);
      this.navigationReleaseTimer = window.setTimeout(() => {
        this.navigationTargetId = null;
        this.navigationReleaseTimer = null;
      }, 250);
    }

    openDrawer() {
      if (!this.drawer || typeof this.drawer.showModal !== 'function') return;

      this.drawer.showModal();
      this.drawerTrigger?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('accessories-catalog--drawer-open');
      this.drawer.querySelector('[data-catalog-drawer-close]')?.focus();
    }

    closeDrawer() {
      if (!this.drawer?.open) return;

      this.drawer.close();
    }
  }

  if (!customElements.get('accessories-catalog')) {
    customElements.define('accessories-catalog', AccessoriesCatalog);
  }
}
